import useLocalStorage from "@rehooks/local-storage";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createEndMessageView } from "../../../../api";
import {
  useCurrentGameId,
  useCurrentGamePicks,
  useCurrentGameQuery,
  useCurrentScoringAndRank,
} from "../../hooks";
import { getScoreByPlayer } from "../../hooks/use-current-score-by-player/calculate-score.ts";
import { MissingPointsDialog } from "./MissingPointsDialog";
import { PostGameMessage } from "./PostGameMessage";

export const MessageController = () => {
  const currentGameId = useCurrentGameId();
  const currentGameQuery = useCurrentGameQuery();
  const scoringQuery = useCurrentScoringAndRank();
  const picksQuery = useCurrentGamePicks();
  const queryClient = useQueryClient();

  const [endMessageViewed, setEndMessageViewed] = useLocalStorage(
    `${currentGameId}::endMessageViewed`,
    false,
  );

  const [missingPointsViewed, setMissingPointsViewed] = useLocalStorage(
    `${currentGameId}::missingPointsViewed`,
    false,
  );

  const viewedEndMessageMutation = useMutation({
    mutationFn: async () => {
      await createEndMessageView(currentGameId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["games", "result-viewed"] });
    },
  });

  if (
    currentGameQuery.isPending ||
    scoringQuery.isPending ||
    picksQuery.isPending
  ) {
    return null;
  }

  if (currentGameQuery.isError || scoringQuery.isError || picksQuery.isError) {
    return null;
  }

  const game = currentGameQuery.data;
  const events = scoringQuery.data.events;
  const rank = scoringQuery.data.rank;
  const picks = picksQuery.data;

  const pointsByPlayerId = getScoreByPlayer(events, picks);

  const missedPoints = picks
    .map((pick) => pick.playerId)
    .reduce((totalMissed, playerId) => {
      const score = pointsByPlayerId[playerId];

      return totalMissed + (score?.missed?.total ?? 0);
    }, 0);

  if (missedPoints > 0 && !missingPointsViewed) {
    return (
      <MissingPointsDialog
        pointsMissed={missedPoints}
        defaultOpen={true}
        onFinishClose={() => {
          setMissingPointsViewed(true);
        }}
      />
    );
  }

  const gameIsFinished = game.status === "finished";

  if (gameIsFinished && !endMessageViewed) {
    return (
      <PostGameMessage
        game={game}
        position={rank.rank || 0}
        defaultOpen={true}
        onFinishClose={() => {
          setEndMessageViewed(true);
          viewedEndMessageMutation.mutate();
        }}
      />
    );
  }
};
